import usdcUsdMaxCsv from './usdc-usd-max.csv';

/** UTC calendar day `YYYY-MM-DD` → USDC price in USD (bundled CoinGecko-style daily close). */
let usdcUsdByUtcDay: Map<string, number> | null = null;
let sortedUtcDayKeys: string[] = [];

function parseUsdcUsdCsv(text: string): Map<string, number> {
  const map = new Map<string, number>();
  const lines = String(text).trim().split(/\r?\n/);
  for (let i = 1; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const firstComma = line.indexOf(',');
    if (firstComma < 0) continue;
    const snapped = line.slice(0, firstComma).trim();
    const rest = line.slice(firstComma + 1);
    const secondComma = rest.indexOf(',');
    const priceStr = secondComma >= 0 ? rest.slice(0, secondComma) : rest;
    const price = parseFloat(priceStr.trim());
    if (!Number.isFinite(price) || price <= 0) continue;
    const dayKey = snapped.slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dayKey)) continue;
    map.set(dayKey, price);
  }
  return map;
}

export function ensureUsdcUsdDailyLoaded(): void {
  if (usdcUsdByUtcDay != null) return;
  usdcUsdByUtcDay = parseUsdcUsdCsv(usdcUsdMaxCsv);
  sortedUtcDayKeys = Array.from(usdcUsdByUtcDay.keys()).sort();
}

/**
 * USD per 1 USDC at UTC calendar day of `date`, or `null` if outside bundled range (exact day only).
 */
export function usdcUsdPerUnitOnUtcDay(date: Date): number | null {
  ensureUsdcUsdDailyLoaded();
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  const key = `${y}-${m}-${d}`;
  const v = usdcUsdByUtcDay!.get(key);
  return v != null ? v : null;
}

/**
 * Same as {@link usdcUsdPerUnitOnUtcDay} with nearest prior day in file if missing (weekends/gaps).
 * Clamps to first/last row when outside range.
 */
export function usdcUsdPerUnitOnUtcDayNearest(date: Date): number | null {
  ensureUsdcUsdDailyLoaded();
  const map = usdcUsdByUtcDay!;
  const keys = sortedUtcDayKeys;
  if (keys.length === 0) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  const dayKey = `${y}-${m}-${d}`;
  const hit = map.get(dayKey);
  if (hit != null) return hit;
  if (dayKey < keys[0]) return map.get(keys[0]) ?? null;
  if (dayKey > keys[keys.length - 1]) return map.get(keys[keys.length - 1]) ?? null;
  let i = keys.length - 1;
  while (i >= 0 && keys[i] > dayKey) i--;
  return i >= 0 ? map.get(keys[i])! : map.get(keys[0])!;
}
